import React from 'react';
import { AlertTriangle, TrendingUp } from 'lucide-react';
import type { ResultadoAcordo } from '../../../src/lib/parcelamentoCalculo.ts';
import { formatarBRL } from '../../../src/lib/parcelamentoCalculo.ts';
import { ORIGENS_DESCONTO, type OrigemDesconto } from '../../../src/lib/parcelamentoRegras.ts';

/**
 * Memória de cálculo do acordo — a peça que o analista confere antes de
 * comprometer o cliente, e a mesma conta que vai impressa no termo.
 *
 * Direção estética: extrato bancário, não dashboard. Números tabulares
 * alinhados, linhas pontilhadas de guia como numa fatura de papel, tipografia
 * apagada nos rótulos e forte nos valores. A densidade é proposital: o analista
 * precisa ver dívida, encargos, entrada e as N parcelas na mesma tela para
 * decidir. Espaçamento generoso aqui viraria rolagem, e rolagem numa conferência
 * de dinheiro é onde o erro passa.
 *
 * Nenhum número é recalculado aqui. Tudo vem pronto do motor
 * (src/lib/parcelamentoCalculo.ts): se a tela fizesse a própria conta, o valor
 * mostrado poderia divergir do que vira boleto — a divergência que o cliente
 * descobre antes da gente.
 */

interface Props {
    resultado: ResultadoAcordo;
    /** Rótulo por fatura (mês de referência), para a composição não virar uma
     *  lista de ids que ninguém reconhece. */
    rotuloFatura?: (id: string | number) => string;
    /** `false` resume a tabela de amortização às 3 primeiras parcelas. */
    detalhado?: boolean;
    /** De onde veio o desconto (#6174). Valor já pago sai ANTES do confessado,
     *  como no termo; desconto da gestão sai depois. Null = sem origem. */
    origemDesconto?: OrigemDesconto | null;
}

const fmtData = (iso: string) => {
    const [a, m, d] = iso.slice(0, 10).split('-');
    return `${d}/${m}/${a}`;
};

/** Linha de composição com guia pontilhada — o gesto que faz a peça ler como
 *  documento contábil em vez de card de dashboard. */
const Linha: React.FC<{
    rotulo: string;
    base?: string;
    valor: number;
    negativo?: boolean;
    destaque?: boolean;
}> = ({ rotulo, base, valor, negativo, destaque }) => (
    <div
        className={`flex items-baseline gap-3 py-2 ${
            destaque
                ? 'border-t-2 border-gray-300 dark:border-gray-600 mt-1 pt-3'
                : 'border-t border-gray-100 dark:border-gray-800'
        }`}
    >
        <span
            className={`text-sm ${
                destaque ? 'font-bold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
            }`}
        >
            {rotulo}
        </span>
        {base && (
            <span className="text-[11px] text-gray-400 dark:text-gray-500 font-mono truncate">{base}</span>
        )}
        <span className="flex-1 border-b border-dotted border-gray-200 dark:border-gray-700 translate-y-[-3px]" />
        <span
            className={`tabular-nums shrink-0 ${
                destaque ? 'text-base font-bold text-gray-900 dark:text-white' : 'text-sm font-semibold'
            } ${negativo ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-800 dark:text-gray-200'}`}
        >
            {negativo ? '− ' : ''}
            {formatarBRL(Math.abs(valor))}
        </span>
    </div>
);

const MemoriaCalculoAcordo: React.FC<Props> = ({ resultado, rotuloFatura, detalhado = true, origemDesconto = null }) => {
    const r = resultado;
    const temEncargos = r.multaTotalCentavos > 0 || r.moraTotalCentavos > 0;
    const temSaldo = r.valorSaldoBrutoCentavos > 0;
    // Memória gravada antes de 08/10/2026 não tem os outros débitos.
    const outros = r.detalheOutrosDebitos ?? [];
    const temOutros = (r.outrosDebitosCentavos ?? 0) > 0;
    // Valor que o cliente já pagou não é desconto: abate a dívida antes do
    // confessado. Só o desconto de verdade aparece depois dele.
    const temPagoAntes = r.descontoDividaCentavos > 0 && origemDesconto === 'valor_ja_pago';
    const temDesconto = r.descontoDividaCentavos > 0 && !temPagoAntes;
    // Com desconto, o custo do financiamento se mede sobre o que foi negociado.
    const baseCusto = r.dividaNegociadaCentavos;
    const ultima = r.parcelas[r.parcelas.length - 1];

    return (
        <div className="space-y-5">
            {/* ---------------------------------------------- composição da dívida */}
            <section>
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">
                    Composição da dívida
                </h4>

                {rotuloFatura && r.detalheFaturas.length > 0 && (
                    <div className="mb-2 rounded-lg bg-gray-50 dark:bg-gray-900/40 px-3 py-2 space-y-1">
                        {r.detalheFaturas.map((f) => (
                            <div key={String(f.id)} className="flex items-baseline gap-2 text-[11px]">
                                <span className="font-mono text-gray-500 dark:text-gray-400 w-20 shrink-0">
                                    {rotuloFatura(f.id)}
                                </span>
                                {f.diasAtraso > 0 ? (
                                    <span className="text-amber-600 dark:text-amber-500 shrink-0">
                                        {f.diasAtraso} d. atraso
                                    </span>
                                ) : (
                                    <span className="text-gray-400 shrink-0">em dia</span>
                                )}
                                <span className="flex-1" />
                                <span className="tabular-nums text-gray-600 dark:text-gray-300">
                                    {formatarBRL(f.valorCentavos)}
                                </span>
                                {(f.multaCentavos > 0 || f.moraCentavos > 0) && (
                                    <span className="tabular-nums text-amber-600 dark:text-amber-500 w-20 text-right">
                                        + {formatarBRL(f.multaCentavos + f.moraCentavos)}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <div>
                    <Linha
                        rotulo="Faturas em aberto e vencidas"
                        base={`${r.detalheFaturas.length} fatura(s)`}
                        valor={r.valorFaturasCentavos}
                    />
                    {temEncargos && (
                        <>
                            <Linha rotulo="Multa moratória" valor={r.multaTotalCentavos} />
                            <Linha rotulo="Juros de mora" base="pro rata die" valor={r.moraTotalCentavos} />
                        </>
                    )}
                    {temSaldo && (
                        <>
                            <Linha rotulo="Créditos de energia" valor={r.valorSaldoBrutoCentavos} />
                            <Linha rotulo="Desconto contratual" valor={r.descontoSaldoCentavos} negativo />
                        </>
                    )}
                    {temOutros && (
                        <>
                            <Linha rotulo="Outros débitos" base={`${outros.length} item(ns)`} valor={r.outrosDebitosCentavos} />
                            <div className="pb-1 space-y-0.5">
                                {outros.map((d, i) => (
                                    <div key={i} className="flex items-baseline gap-2 pl-3 text-[11px] text-gray-500 dark:text-gray-400">
                                        <span className="truncate">{d.descricao}</span>
                                        <span className="flex-1" />
                                        <span className="tabular-nums">{formatarBRL(d.valorCentavos)}</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                    {temPagoAntes && (
                        <Linha rotulo={ORIGENS_DESCONTO.valor_ja_pago.linha} valor={r.descontoDividaCentavos} negativo />
                    )}
                    <Linha
                        rotulo="Valor confessado"
                        valor={temPagoAntes ? r.dividaNegociadaCentavos : r.dividaBrutaCentavos}
                        destaque
                    />
                    {temDesconto && (
                        <>
                            <Linha
                                rotulo={origemDesconto === 'gestao' ? ORIGENS_DESCONTO.gestao.linha : 'Desconto concedido'}
                                base={`${r.descontoDividaPercentual.toFixed(2).replace('.', ',')}%`}
                                valor={r.descontoDividaCentavos}
                                negativo
                            />
                            <Linha rotulo="Valor negociado" valor={r.dividaNegociadaCentavos} destaque />
                        </>
                    )}
                </div>
            </section>

            {/* ------------------------------------------ entrada e financiamento */}
            <section className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-primary">Entrada</p>
                    <p className="text-xl font-bold tabular-nums text-gray-900 dark:text-white mt-0.5">
                        {formatarBRL(r.entradaCentavos)}
                    </p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        {r.entradaPercentual.toFixed(2).replace('.', ',')}% · vence {fmtData(r.vencimentoEntrada)}
                    </p>
                </div>
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">A financiar</p>
                    <p className="text-xl font-bold tabular-nums text-gray-900 dark:text-white mt-0.5">
                        {formatarBRL(r.valorFinanciadoCentavos)}
                    </p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        {r.numeroParcelas > 0
                            ? `${r.numeroParcelas}× a ${String(r.taxaJurosMensalPercent).replace('.', ',')}% a.m.`
                            : 'quitação à vista'}
                    </p>
                </div>
            </section>

            {/* ------------------------------------------ tabela de amortização */}
            {r.parcelas.length > 0 && (
                <section>
                    <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">
                        Tabela de amortização
                        <span className="ml-2 font-normal normal-case tracking-normal text-gray-400">
                            juros sobre o saldo devedor decrescente
                        </span>
                    </h4>
                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="max-h-56 overflow-y-auto">
                            <table className="w-full text-[11px] tabular-nums">
                                <thead className="bg-gray-50 dark:bg-gray-900/60 sticky top-0">
                                    <tr className="text-gray-500 dark:text-gray-400">
                                        <th className="text-left font-semibold px-3 py-1.5">#</th>
                                        <th className="text-left font-semibold px-2 py-1.5">Vencimento</th>
                                        <th className="text-right font-semibold px-2 py-1.5">Saldo inicial</th>
                                        <th className="text-right font-semibold px-2 py-1.5">Juros</th>
                                        <th className="text-right font-semibold px-2 py-1.5">Amortização</th>
                                        <th className="text-right font-semibold px-3 py-1.5">Parcela</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                    {/* Linha 00 = entrada, como na tela da Equatorial anexada ao
                                        chamado. Mantê-la aqui deixa o cronograma completo num lugar só. */}
                                    <tr className="bg-primary/5">
                                        <td className="px-3 py-1.5 font-bold text-primary">00</td>
                                        <td className="px-2 py-1.5 text-gray-600 dark:text-gray-300">
                                            {fmtData(r.vencimentoEntrada)}
                                        </td>
                                        <td className="px-2 py-1.5 text-right text-gray-400">—</td>
                                        <td className="px-2 py-1.5 text-right text-gray-400">—</td>
                                        <td className="px-2 py-1.5 text-right text-gray-400">—</td>
                                        <td className="px-3 py-1.5 text-right font-bold text-primary">
                                            {formatarBRL(r.entradaCentavos)}
                                        </td>
                                    </tr>
                                    {(detalhado ? r.parcelas : r.parcelas.slice(0, 3)).map((p) => (
                                        <tr key={p.numeroParcela} className="text-gray-700 dark:text-gray-300">
                                            <td className="px-3 py-1.5 font-semibold">
                                                {String(p.numeroParcela).padStart(2, '0')}
                                            </td>
                                            <td className="px-2 py-1.5">{fmtData(p.dataVencimento)}</td>
                                            <td className="px-2 py-1.5 text-right text-gray-500">
                                                {formatarBRL(p.saldoInicialCentavos)}
                                            </td>
                                            <td className="px-2 py-1.5 text-right text-amber-600 dark:text-amber-500">
                                                {formatarBRL(p.jurosCentavos)}
                                            </td>
                                            <td className="px-2 py-1.5 text-right text-gray-500">
                                                {formatarBRL(p.amortizacaoCentavos)}
                                            </td>
                                            <td className="px-3 py-1.5 text-right font-semibold text-gray-900 dark:text-white">
                                                {formatarBRL(p.valorCentavos)}
                                            </td>
                                        </tr>
                                    ))}
                                    {!detalhado && r.parcelas.length > 3 && (
                                        <tr>
                                            <td colSpan={6} className="px-3 py-1.5 text-center text-gray-400 italic">
                                                … mais {r.parcelas.length - 3} parcela(s)
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            )}

            {/* ------------------------------------------------------- totais */}
            <section className="rounded-xl bg-gray-900 dark:bg-black text-white p-4">
                <div className="flex items-baseline justify-between">
                    <span className="text-xs uppercase tracking-wider text-gray-400">Total do acordo</span>
                    <span className="text-2xl font-bold tabular-nums">{formatarBRL(r.totalAcordoCentavos)}</span>
                </div>
                <div className="mt-2 pt-2 border-t border-white/10 flex items-center gap-2 text-[11px] text-gray-400">
                    <TrendingUp size={12} className="shrink-0" />
                    <span>
                        Custo do financiamento para o cliente:{' '}
                        <strong className="text-amber-400 tabular-nums">
                            {formatarBRL(r.custoFinanciamentoCentavos)}
                        </strong>
                        {baseCusto > 0 && (
                            <>
                                {' '}
                                ({((r.custoFinanciamentoCentavos / baseCusto) * 100)
                                    .toFixed(1)
                                    .replace('.', ',')}
                                % sobre o {temDesconto ? 'negociado' : 'confessado'})
                            </>
                        )}
                    </span>
                </div>
            </section>

            {/* A última parcela difere das outras de propósito. Dizer isso aqui evita
                o chamado "a última parcela veio com centavo a mais". */}
            {/* O texto vai dentro de um <span>: num contêiner flex, cada trecho solto e
                cada interpolação viraria um item de flex próprio e a frase se espalharia
                em colunas em vez de fluir. */}
            {r.parcelas.length > 1 && ultima.valorCentavos !== r.parcelas[0].valorCentavos && (
                <div className="flex items-start gap-2 text-[11px] text-gray-500 dark:text-gray-400">
                    <AlertTriangle size={12} className="mt-0.5 shrink-0 text-gray-400" />
                    <span>
                        A última parcela ({formatarBRL(ultima.valorCentavos)}) difere das demais porque fecha o
                        saldo devedor exatamente, sem sobra de centavos.
                    </span>
                </div>
            )}
        </div>
    );
};

export default MemoriaCalculoAcordo;
